import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PersonalityQuiz = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [quizStarted, setQuizStarted] = useState(false);
  const navigate = useNavigate();

  // Mapping des profils vers les IDs de filières réelles (pour la cohérence)
  // Scientifique -> sciences-math, sciences-ex
  // Technique -> technique
  // Économique -> economique
  // Littéraire -> litteraire

  const questions = [
    {
      id: 1,
      question: "Quel type d'activité vous passionne le plus ?",
      options: [
        { id: 'a', text: 'Résoudre des énigmes logiques et mathématiques', scores: { 'sciences-math': 2, 'technique': 1 } },
        { id: 'b', text: 'Comprendre comment fonctionnent les objets et machines', scores: { 'technique': 2, 'sciences-ex': 1 } },
        { id: 'c', text: 'Suivre l\'actualité économique et gérer un budget', scores: { 'economique': 2 } },
        { id: 'd', text: 'Lire, écrire et débattre des idées', scores: { 'litteraire': 2 } }
      ]
    },
    {
      id: 2,
      question: "Dans un projet de groupe, quel rôle prenez-vous ?",
      options: [
        { id: 'a', text: 'L\'expert qui analyse les données', scores: { 'sciences-math': 1, 'sciences-ex': 1 } },
        { id: 'b', text: 'Le technicien qui s\'occupe de la mise en œuvre', scores: { 'technique': 2 } },
        { id: 'c', text: 'Le gestionnaire qui planifie et organise', scores: { 'economique': 2 } },
        { id: 'd', text: 'Le communicant qui présente et rédige', scores: { 'litteraire': 2 } }
      ]
    },
    {
      id: 3,
      question: "Quelle matière vous semble la plus utile ?",
      options: [
        { id: 'a', text: 'Les Mathématiques (pour tout modéliser)', scores: { 'sciences-math': 2, 'economique': 1 } },
        { id: 'b', text: 'La Physique ou la SI (pour comprendre le réel)', scores: { 'sciences-ex': 1, 'technique': 2 } },
        { id: 'c', text: 'L\'Histoire-Géo et les langues (pour comprendre le monde)', scores: { 'litteraire': 2, 'economique': 1 } },
        { id: 'd', text: 'La SVT (pour comprendre le vivant)', scores: { 'sciences-ex': 2 } }
      ]
    },
    {
      id: 4,
      question: "Votre environnement de travail idéal plus tard ?",
      options: [
        { id: 'a', text: 'Un laboratoire de recherche ou un bureau d\'études', scores: { 'sciences-math': 1, 'sciences-ex': 2 } },
        { id: 'b', text: 'Un atelier, un chantier ou une usine moderne', scores: { 'technique': 2 } },
        { id: 'c', text: 'Une grande entreprise, une banque ou un cabinet', scores: { 'economique': 2 } },
        { id: 'd', text: 'Une rédaction, un tribunal ou une école', scores: { 'litteraire': 2 } }
      ]
    },
    {
      id: 5,
      question: "Face à un problème complexe, vous...",
      options: [
        { id: 'a', text: 'Cherchez une formule ou une méthode logique', scores: { 'sciences-math': 2 } },
        { id: 'b', text: 'Essayez de bricoler une solution pratique', scores: { 'technique': 2 } },
        { id: 'c', text: 'Analysez les coûts et les avantages', scores: { 'economique': 2 } },
        { id: 'd', text: 'Cherchez des exemples historiques ou des textes', scores: { 'litteraire': 2 } }
      ]
    }
  ];

  const handleAnswer = (optionId) => {
    const newAnswers = { ...answers, [currentQuestion]: optionId };
    setAnswers(newAnswers);

    // Petit délai pour l'UX
    setTimeout(() => {
        if (currentQuestion < questions.length - 1) {
            setCurrentQuestion(currentQuestion + 1);
        } else {
            setShowResults(true);
        }
    }, 250);
  };

  const getRecommendedTracks = () => {
    const scores = {
      'sciences-math': 0,
      'sciences-ex': 0,
      'technique': 0,
      'economique': 0,
      'litteraire': 0
    };

    // Calcul des scores
    Object.entries(answers).forEach(([qIndex, optId]) => {
      const question = questions[qIndex];
      const option = question.options.find(o => o.id === optId);
      if (option && option.scores) {
        Object.entries(option.scores).forEach(([track, score]) => {
            scores[track] += score;
        });
      }
    });

    // Conversion en tableau trié
    const maxScorePossible = questions.length * 2; // Estimation grossière
    return Object.entries(scores)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3) // Top 3
      .map(([trackId, score]) => {
         // Mapping ID -> Nom affiché
         let name = "";
         if(trackId === 'sciences-math') name = "Sciences Mathématiques";
         if(trackId === 'sciences-ex') name = "Sciences Expérimentales";
         if(trackId === 'technique') name = "Sciences & Technologies";
         if(trackId === 'economique') name = "Sciences Économiques";
         if(trackId === 'litteraire') name = "Lettres & Sc. Humaines";

         return {
             trackId,
             name,
             score,
             percentage: Math.min(100, Math.round((score / 8) * 100)) // Score sur une base arbitraire pour l'affichage
         };
      });
  };

  const resetQuiz = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setQuizStarted(false);
  };

  const handleExploreTrack = (trackId) => {
    // Redirection vers la base de données avec le filtre approprié
    // On mappe les IDs vers des termes de recherche simples
    let searchTerm = "";
    if(trackId === 'sciences-math') searchTerm = "Ingénierie";
    if(trackId === 'sciences-ex') searchTerm = "Santé";
    if(trackId === 'technique') searchTerm = "Technologie";
    if(trackId === 'economique') searchTerm = "Commerce";
    if(trackId === 'litteraire') searchTerm = "Droit";

    navigate(`/post-bac-database?search=${searchTerm}`);
  };

  if (!quizStarted) {
    return (
      <div className="bg-card border border-border rounded-xl p-8 shadow-sm text-center font-sans animate-fade-in">
        <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="Brain" size={40} className="text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-3">Quiz d'Orientation Rapide</h3>
        <p className="text-muted-foreground mb-8 max-w-md mx-auto">
          Répondez à 5 questions simples pour découvrir quelle filière du bac marocain correspond le mieux à votre profil.
        </p>
        <Button 
          variant="default" 
          size="lg"
          onClick={() => setQuizStarted(true)}
          iconName="Play"
          className="w-full sm:w-auto px-8"
        >
          Commencer le test
        </Button>
      </div>
    );
  }

  if (showResults) {
    const recommendedTracks = getRecommendedTracks();
    const topTrack = recommendedTracks[0];

    return (
      <div className="bg-card border border-border rounded-xl p-6 shadow-sm font-sans animate-fade-in">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-sm">
            <Icon name="CheckCircle" size={32} className="text-green-600" />
          </div>
          <h3 className="text-2xl font-bold text-foreground mb-2">Résultat : {topTrack.name}</h3>
          <p className="text-muted-foreground">Cette filière semble parfaitement adaptée à vos goûts !</p>
        </div>

        <div className="space-y-4 mb-8">
          {recommendedTracks.map((result, index) => (
            <div key={index} className="flex items-center justify-between p-4 bg-muted/20 border border-border/50 rounded-xl hover:bg-muted/40 transition-colors">
              <div className="flex items-center space-x-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  index === 0 ? 'bg-primary text-white shadow-md' : 'bg-muted text-muted-foreground'
                }`}>
                  {index + 1}
                </div>
                <div>
                  <div className="font-bold text-foreground">{result.name}</div>
                  <div className="text-xs text-muted-foreground font-medium">Affinité : {result.percentage}%</div>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => handleExploreTrack(result.trackId)}
                iconName="ArrowRight"
                className="text-primary hover:text-primary hover:bg-primary/10"
              >
                Explorer
              </Button>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button 
            variant="outline" 
            onClick={resetQuiz}
            iconName="RotateCcw"
            className="flex-1"
          >
            Refaire le test
          </Button>
          <Button 
            variant="default"
            onClick={() => navigate('/student-profile')} // Placeholder pour la sauvegarde
            iconName="User"
            className="flex-1"
          >
            Voir mon profil
          </Button>
        </div>
      </div>
    );
  }

  const currentQ = questions[currentQuestion];
  const progress = ((currentQuestion + 1) / questions.length) * 100;

  return (
    <div className="bg-card border border-border rounded-xl p-6 shadow-sm font-sans animate-fade-in">
      {/* Progress Bar */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold text-muted-foreground uppercase tracking-wider">Question {currentQuestion + 1} / {questions.length}</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
          <div 
            className="bg-primary h-full rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>
      </div>

      {/* Question */}
      <div className="mb-8 animate-fade-in-up">
        <h3 className="text-xl font-bold text-foreground mb-6 leading-relaxed">{currentQ.question}</h3>
        <div className="space-y-3">
          {currentQ.options.map((option) => (
            <button
              key={option.id}
              onClick={() => handleAnswer(option.id)}
              className="w-full p-4 text-left bg-background hover:bg-muted/50 border border-border hover:border-primary/50 rounded-xl transition-all duration-200 group flex items-center justify-between shadow-sm hover:shadow-md active:scale-[0.99]"
            >
              <span className="text-foreground font-medium group-hover:text-primary transition-colors">{option.text}</span>
              <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 group-hover:border-primary flex items-center justify-center transition-colors">
                <div className="w-2.5 h-2.5 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Navigation (Back) */}
      {currentQuestion > 0 && (
        <button 
            onClick={() => setCurrentQuestion(currentQuestion - 1)}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center transition-colors"
        >
            <Icon name="ArrowLeft" size={14} className="mr-1" />
            Question précédente
        </button>
      )}
    </div>
  );
};

export default PersonalityQuiz;